// services/ugc/commentsApi.ts
import baseApi from "../../lib/baseApi";
import {
  CommentDTO,
  CommentSummary,
  CommentCreatePayload,
  CommentUpdatePayload,
} from "../../types/ugc/commentsTypes";

/** DRF PageNumberPagination shape */
export type Paginated<T> = {
  count: number;
  next: string | null;
  previous: string | null;
  results: T[];
};

/** Root item for paginated roots (no 'responses', includes replies_count) */
export type RootCommentDTO = Omit<CommentDTO, "responses"> & {
  replies_count: number;
};

/** ---------- Comments API (typed, simple) ---------- */
export const commentsApi = {
  /** Flat list (optional) — unchanged */
  listByObject: async (contentType: string, objectId: number) => {
    const { data } = await baseApi.get<CommentDTO[]>(
      `/posts/comments/?content_type=${encodeURIComponent(contentType)}&object_id=${objectId}`
    );
    return data;
  },

  /** Thread list (roots + one-level replies) — unchanged */
  listThreadByObject: async (contentType: string, objectId: number) => {
    const { data } = await baseApi.get<CommentDTO[]>(
      `/posts/comments/thread/?content_type=${encodeURIComponent(contentType)}&object_id=${objectId}`
    );
    return data;
  },

  /** 🔹 NEW: Paginated roots ONLY (no replies) with replies_count */
  listThreadPage: async (
    contentType: string,
    objectId: number,
    page?: number,
    pageSize: number = 10 // mobile-first default
  ) => {
    const params = new URLSearchParams({
      content_type: contentType,
      object_id: String(objectId),
      page_size: String(pageSize),
    });
    if (page && page > 1) params.set("page", String(page));

    const { data } = await baseApi.get<Paginated<RootCommentDTO>>(
      `/posts/comments/thread_page/?${params.toString()}`
    );
    return data;
  },

  /** 🔹 NEW: Paginated replies for a given root */
  listRepliesPage: async (
    parentId: number,
    page?: number,
    pageSize: number = 10 // mobile-first default
  ) => {
    const params = new URLSearchParams({
      parent_id: String(parentId),
      page_size: String(pageSize),
    });
    if (page && page > 1) params.set("page", String(page));

    const { data } = await baseApi.get<Paginated<CommentDTO>>(
      `/posts/comments/replies/?${params.toString()}`
    );
    return data;
  },

  /** Create (returns saved CommentDTO) — unchanged */
  create: async (payload: CommentCreatePayload) => {
    const { data } = await baseApi.post<CommentDTO>(`/posts/comments/`, payload);
    return data;
  },

  /** Update (returns saved CommentDTO) — unchanged */
  update: async (id: number, payload: Partial<CommentUpdatePayload>) => {
    const { data } = await baseApi.patch<CommentDTO>(`/posts/comments/${id}/`, payload);
    return data;
  },

  /** Delete (owner-only) — unchanged */
  remove: async (id: number) => {
    const { status } = await baseApi.delete(`/posts/comments/${id}/`);
    return status === 204;
  },

  /** Summary — unchanged */
  summaryByObject: async (contentType: string, objectId: number) => {
    const { data } = await baseApi.get<CommentSummary>(
      `/posts/comments/summary/?content_type=${encodeURIComponent(contentType)}&object_id=${objectId}`
    );
    return data;
  },
};
